'use client';

import { c as _c } from "react/compiler-runtime";
import './child-gifts.scss';
import type { PropsWithChildren } from 'react';
import React, { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import useCollapsible from '@sola/ark-core/utils/hooks/useCollapsible.ts';
import { useRouter } from '@sola/ark-core/utils/hooks/useRouter.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { knownMyUrls } from '@sola/my-sdk/constants.ts';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import { GiftCommitmentCard, GiftCommitmentCardSkeleton } from '../../molecules/gift-commitment-card/gift-commitment-card.tsx';
import { GiftsTotal } from '../../molecules/gifts-total/gifts-total.tsx';
import type { ChildGiftOption } from '../../types/gifts.ts';
import { GiftOptionsDialog } from '../gift-options-dialog/gift-options-dialog.tsx';
import { type RemovedGift, type SelectedGiftOptions, getCardElementKey, useChildGiftsLogic } from './child-gifts.logic.ts';
const getTranslationKeyGiftType = (giftType: string) => `my:gifts.${giftType}.section`;
interface ChildGiftsAlertProps {
  text?: React.ReactNode;
  isLoading?: boolean;
}
const ChildGiftsAlert: React.FC<ChildGiftsAlertProps> = t0 => {
  const $ = _c(3);
  const {
    text,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? true : t1;
  if (!text) {
    return;
  }
  if (isLoading) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Skeleton height="4rem" />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] !== text) {
    t2 = <Alert variant="neutral" message={text} />;
    $[1] = text;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
interface ChildGiftsDescriptionProps {
  text: string;
}
const ChildGiftsDescription: React.FC<ChildGiftsDescriptionProps> = t0 => {
  const $ = _c(2);
  const {
    text
  } = t0;
  let t1;
  if ($[0] !== text) {
    t1 = <Text size={Sizes.Small} asChild={true}><p className="body-neutral-mild">{text}</p></Text>;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface ChildGiftsSectionProps extends PropsWithChildren {
  title?: string;
}
const ChildGiftsSection: React.FC<ChildGiftsSectionProps> = t0 => {
  const $ = _c(21);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      defaultExpanded: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    triggerProps,
    targetProps
  } = useCollapsible(t1);
  let contentTargetProps;
  let targetClasses;
  let targetHidden;
  if ($[1] !== targetProps) {
    ({
      className: targetClasses,
      hide: targetHidden,
      ...contentTargetProps
    } = targetProps);
    $[1] = targetProps;
    $[2] = contentTargetProps;
    $[3] = targetClasses;
    $[4] = targetHidden;
  } else {
    contentTargetProps = $[2];
    targetClasses = $[3];
    targetHidden = $[4];
  }
  let t2;
  if ($[5] !== title) {
    t2 = title && <Text variant={Variants.Heading} size={Sizes.Small} asChild={true}><h3 className="ChildGiftsInfoSection__title">{title}</h3></Text>;
    $[5] = title;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = targetHidden ? "180deg" : "0deg";
  let t4;
  if ($[7] !== t3) {
    t4 = <ArkIcon iconName="chevron-up" className="ChildGiftsInfoSection__chevron shrink-0" size="1.5rem" style={{
      rotate: t3
    }} />;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4 || $[11] !== triggerProps) {
    t5 = <button type="button" className="ChildGiftsInfoSection__header flex items-center justify-between gap-comfy-xs md:gap-comfy-sm w-full py-snug-lg" {...triggerProps}>{t2}{t4}</button>;
    $[9] = t2;
    $[10] = t4;
    $[11] = triggerProps;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== children || $[14] !== contentTargetProps || $[15] !== targetClasses || $[16] !== targetHidden) {
    t6 = !targetHidden && <div className={cn("flex flex-col gap-comfy-xl", targetClasses)} {...contentTargetProps}>{children}</div>;
    $[13] = children;
    $[14] = contentTargetProps;
    $[15] = targetClasses;
    $[16] = targetHidden;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <section>{t5}{t6}</section>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
interface ChildGiftsCommitmentListProps {
  giftType: GiftTypeEnum;
  childGiftOptions: ChildGiftOption[];
  isLoading: boolean;
  onGiftOptionsOpen: (selectedGiftOptions: SelectedGiftOptions) => void;
  onGiftRemove: (gift: RemovedGift) => Promise<void>;
}
const ChildGiftsCommitmentList: React.FC<ChildGiftsCommitmentListProps> = t0 => {
  const $ = _c(12);
  const {
    giftType,
    childGiftOptions,
    isLoading,
    onGiftOptionsOpen,
    onGiftRemove
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex flex-col gap-comfy-xs"><GiftCommitmentCardSkeleton /><GiftCommitmentCardSkeleton /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (childGiftOptions.length === 0) {
    return null;
  }
  let t1;
  if ($[1] !== childGiftOptions || $[2] !== giftType || $[3] !== onGiftOptionsOpen || $[4] !== onGiftRemove) {
    let t2;
    if ($[6] !== giftType || $[7] !== onGiftOptionsOpen || $[8] !== onGiftRemove) {
      t2 = t3 => {
        const {
          commitment,
          giftOption
        } = t3;
        const cardElementKey = getCardElementKey(commitment.participant?.globalId ?? "", giftType);
        return <div key={cardElementKey} id={cardElementKey}><GiftCommitmentCard commitment={commitment} giftType={giftType} giftOption={giftOption} onGiftOptionsOpen={() => onGiftOptionsOpen({
            commitment,
            giftOption,
            giftType
          })} onGiftRemove={onGiftRemove} /></div>;
      };
      $[6] = giftType;
      $[7] = onGiftOptionsOpen;
      $[8] = onGiftRemove;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    t1 = childGiftOptions.map(t2);
    $[1] = childGiftOptions;
    $[2] = giftType;
    $[3] = onGiftOptionsOpen;
    $[4] = onGiftRemove;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[10] !== t1) {
    t2 = <div className="flex flex-col gap-comfy-xs">{t1}</div>;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
const GiftsSkeleton: React.FC = () => {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div><Skeleton height={34} className="mb-comfy-xs" /><Skeleton height={24} /></div>;
    t1 = <Skeleton height={64} />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-col gap-comfy-xl">{t0}{t1}<div className="flex flex-col gap-comfy-xs"><GiftCommitmentCardSkeleton /><GiftCommitmentCardSkeleton /></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

/**
 * Displays a list of options for giving gifts to commitments.
 */
const ChildGiftsContent: React.FC = () => {
  const $ = _c(71);
  const {
    t
  } = useTranslation("my");
  const {
    router
  } = useRouter();
  const {
    isGiftListLoading,
    isError,
    isContentSettingsLoading,
    hasCommitments,
    contentSettings,
    childGiftOptions,
    shouldShowFinalGiftSection,
    shouldShowOtherGiftSections,
    activeSelection,
    selectedGiftTypeLimits,
    handleGiftOptionsOpen,
    handleGiftOptionsSubmit,
    handleGiftRemove,
    handleGiftOptionsOpenChange
  } = useChildGiftsLogic();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getTranslationKeyGiftType("birthday");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const birthdayTranslationKey = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getTranslationKeyGiftType("child");
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const childTranslationKey = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getTranslationKeyGiftType("family");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const familyTranslationKey = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getTranslationKeyGiftType("center");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const centerTranslationKey = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = getTranslationKeyGiftType(GiftTypeEnum.Final.toLowerCase());
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const finalTranslationKey = t4;
  let t5;
  let t6;
  if ($[5] !== hasCommitments || $[6] !== isError || $[7] !== isGiftListLoading || $[8] !== router) {
    t5 = () => {
      if (!isGiftListLoading && !isError && !hasCommitments) {
        router.replace(knownMyUrls.sponsorshipsNew);
      }
    };
    t6 = [isGiftListLoading, isError, hasCommitments, router];
    $[5] = hasCommitments;
    $[6] = isError;
    $[7] = isGiftListLoading;
    $[8] = router;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  if (isError) {
    let t7;
    if ($[11] !== t) {
      t7 = t("my:gifts.requestError.title");
      $[11] = t;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== t) {
      t8 = t("my:gifts.requestError.message");
      $[13] = t;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== t) {
      t9 = t("my:ctas.contactSupport.url");
      $[15] = t;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] !== t) {
      t10 = t("my:ctas.contactSupport.text");
      $[17] = t;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    let t11;
    if ($[19] !== t10 || $[20] !== t9) {
      t11 = <Link href={t9}>{t10}</Link>;
      $[19] = t10;
      $[20] = t9;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    let t12;
    if ($[22] !== t11 || $[23] !== t7 || $[24] !== t8) {
      t12 = <Alert title={t7} message={t8} variant="negative" action={t11} />;
      $[22] = t11;
      $[23] = t7;
      $[24] = t8;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    return t12;
  }
  if (isGiftListLoading || !hasCommitments) {
    let t7;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <GiftsSkeleton />;
      $[26] = t7;
    } else {
      t7 = $[26];
    }
    return t7;
  }
  let t7;
  if ($[27] !== childGiftOptions?.Final || $[28] !== contentSettings?.giftAmountsFinalMax || $[29] !== contentSettings?.giftAmountsFinalMin || $[30] !== handleGiftOptionsOpen || $[31] !== handleGiftRemove || $[32] !== isContentSettingsLoading || $[33] !== isGiftListLoading || $[34] !== shouldShowFinalGiftSection || $[35] !== t) {
    t7 = shouldShowFinalGiftSection && <><ChildGiftsSection title={t(`${finalTranslationKey}.title`)}><ChildGiftsDescription text={t(`${finalTranslationKey}.message`)} /><ChildGiftsAlert text={<IntlBlock i18nKey={`${finalTranslationKey}.alertText`} values={{
          min: t("core:numbers.currencyCustom", {
            amount: contentSettings?.giftAmountsFinalMin ?? 10
          }),
          max: t("core:numbers.currencyCustom", {
            amount: contentSettings?.giftAmountsFinalMax ?? 3000
          })
        }} />} isLoading={isContentSettingsLoading} /><ChildGiftsCommitmentList giftType={GiftTypeEnum.Final} childGiftOptions={childGiftOptions?.Final ?? []} isLoading={isGiftListLoading} onGiftOptionsOpen={handleGiftOptionsOpen} onGiftRemove={handleGiftRemove} /></ChildGiftsSection><Divider className="my-comfy-xl" purpose="neutral" /></>;
    $[27] = childGiftOptions?.Final;
    $[28] = contentSettings?.giftAmountsFinalMax;
    $[29] = contentSettings?.giftAmountsFinalMin;
    $[30] = handleGiftOptionsOpen;
    $[31] = handleGiftRemove;
    $[32] = isContentSettingsLoading;
    $[33] = isGiftListLoading;
    $[34] = shouldShowFinalGiftSection;
    $[35] = t;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== childGiftOptions?.Birthday || $[38] !== childGiftOptions?.Center || $[39] !== childGiftOptions?.Child || $[40] !== childGiftOptions?.Family || $[41] !== contentSettings?.giftAmountsBirthdayMax || $[42] !== contentSettings?.giftAmountsBirthdayMin || $[43] !== contentSettings?.giftAmountsCenterMax || $[44] !== contentSettings?.giftAmountsChildMax || $[45] !== contentSettings?.giftAmountsChildMin || $[46] !== contentSettings?.giftAmountsFamilyMax || $[47] !== handleGiftOptionsOpen || $[48] !== handleGiftRemove || $[49] !== isContentSettingsLoading || $[50] !== isGiftListLoading || $[51] !== shouldShowOtherGiftSections || $[52] !== t) {
    t8 = shouldShowOtherGiftSections && <><ChildGiftsSection title={t(`${birthdayTranslationKey}.title`)}><ChildGiftsDescription text={t(`${birthdayTranslationKey}.message`)} /><ChildGiftsAlert text={<IntlBlock i18nKey={`${birthdayTranslationKey}.alertText`} values={{
          min: t("core:numbers.currencyCustom", {
            amount: contentSettings?.giftAmountsBirthdayMin ?? 10
          }),
          max: t("core:numbers.currencyCustom", {
            amount: contentSettings?.giftAmountsBirthdayMax ?? 150
          })
        }} />} isLoading={isContentSettingsLoading} /><ChildGiftsCommitmentList giftType={GiftTypeEnum.Birthday} childGiftOptions={childGiftOptions?.Birthday ?? []} isLoading={isGiftListLoading} onGiftOptionsOpen={handleGiftOptionsOpen} onGiftRemove={handleGiftRemove} /></ChildGiftsSection><Divider className="my-comfy-xl" purpose="neutral" /><ChildGiftsSection title={t(`${childTranslationKey}.title`)}><ChildGiftsDescription text={t(`${childTranslationKey}.message`)} /><ChildGiftsAlert text={<IntlBlock i18nKey={`${childTranslationKey}.alertText`} values={{
          min: t("core:numbers.currencyCustom", {
            amount: contentSettings?.giftAmountsChildMin ?? 10
          }),
          max: t("core:numbers.currencyCustom", {
            amount: contentSettings?.giftAmountsChildMax ?? 150
          })
        }} />} isLoading={isContentSettingsLoading} /><ChildGiftsCommitmentList giftType={GiftTypeEnum.Child} childGiftOptions={childGiftOptions?.Child ?? []} isLoading={isGiftListLoading} onGiftOptionsOpen={handleGiftOptionsOpen} onGiftRemove={handleGiftRemove} /></ChildGiftsSection><Divider className="my-comfy-xl" purpose="neutral" /><ChildGiftsSection title={t(`${familyTranslationKey}.title`)}><ChildGiftsDescription text={t(`${familyTranslationKey}.message`)} /><ChildGiftsAlert text={<IntlBlock i18nKey={`${familyTranslationKey}.alertText`} values={{
          max: t("core:numbers.currencyCustom", {
            amount: contentSettings?.giftAmountsFamilyMax ?? 1100
          })
        }} />} isLoading={isContentSettingsLoading} /><ChildGiftsCommitmentList giftType={GiftTypeEnum.Family} childGiftOptions={childGiftOptions?.Family ?? []} isLoading={isGiftListLoading} onGiftOptionsOpen={handleGiftOptionsOpen} onGiftRemove={handleGiftRemove} /></ChildGiftsSection><Divider className="my-comfy-xl" purpose="neutral" /><ChildGiftsSection title={t(`${centerTranslationKey}.title`)}><ChildGiftsDescription text={t(`${centerTranslationKey}.message`)} /><ChildGiftsAlert text={<IntlBlock i18nKey={`${centerTranslationKey}.alertText`} values={{
          giftsMax: 4,
          amountMax: t("core:numbers.currencyCustom", {
            amount: contentSettings?.giftAmountsCenterMax || 15000
          })
        }} />} isLoading={isContentSettingsLoading} /><ChildGiftsCommitmentList giftType={GiftTypeEnum.Center} childGiftOptions={childGiftOptions?.Center ?? []} isLoading={isGiftListLoading} onGiftOptionsOpen={handleGiftOptionsOpen} onGiftRemove={handleGiftRemove} /></ChildGiftsSection><Divider className="my-comfy-xl" purpose="neutral" /></>;
    $[37] = childGiftOptions?.Birthday;
    $[38] = childGiftOptions?.Center;
    $[39] = childGiftOptions?.Child;
    $[40] = childGiftOptions?.Family;
    $[41] = contentSettings?.giftAmountsBirthdayMax;
    $[42] = contentSettings?.giftAmountsBirthdayMin;
    $[43] = contentSettings?.giftAmountsCenterMax;
    $[44] = contentSettings?.giftAmountsChildMax;
    $[45] = contentSettings?.giftAmountsChildMin;
    $[46] = contentSettings?.giftAmountsFamilyMax;
    $[47] = handleGiftOptionsOpen;
    $[48] = handleGiftRemove;
    $[49] = isContentSettingsLoading;
    $[50] = isGiftListLoading;
    $[51] = shouldShowOtherGiftSections;
    $[52] = t;
    $[53] = t8;
  } else {
    t8 = $[53];
  }
  let t9;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <GiftsTotal />;
    $[54] = t9;
  } else {
    t9 = $[54];
  }
  let t10;
  if ($[55] !== t) {
    t10 = t("my:gifts.giftGivingDisclaimer");
    $[55] = t;
    $[56] = t10;
  } else {
    t10 = $[56];
  }
  let t11;
  if ($[57] !== t10) {
    t11 = <Text variant={Variants.Label} asChild={true}><p className="body-neutral-mild mt-comfy-xl">{t10}</p></Text>;
    $[57] = t10;
    $[58] = t11;
  } else {
    t11 = $[58];
  }
  let t12;
  if ($[59] !== t11 || $[60] !== t7 || $[61] !== t8) {
    t12 = <div className="GiftGiving mt-comfy-xl">{t7}{t8}{t9}{t11}</div>;
    $[59] = t11;
    $[60] = t7;
    $[61] = t8;
    $[62] = t12;
  } else {
    t12 = $[62];
  }
  let t13;
  if ($[63] !== activeSelection || $[64] !== handleGiftOptionsOpenChange || $[65] !== handleGiftOptionsSubmit || $[66] !== selectedGiftTypeLimits) {
    t13 = activeSelection && <GiftOptionsDialog commitment={activeSelection.commitment} giftType={activeSelection.giftType} giftTypeLimits={selectedGiftTypeLimits} initialAmount={activeSelection.giftOption.cartItem?.amount} initialFrequency={activeSelection.giftOption.cartItem?.frequency} buttonAction={activeSelection.giftOption.buttonAction ?? undefined} open={true} onOpenChange={handleGiftOptionsOpenChange} onSubmit={handleGiftOptionsSubmit} />;
    $[63] = activeSelection;
    $[64] = handleGiftOptionsOpenChange;
    $[65] = handleGiftOptionsSubmit;
    $[66] = selectedGiftTypeLimits;
    $[67] = t13;
  } else {
    t13 = $[67];
  }
  let t14;
  if ($[68] !== t12 || $[69] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[68] = t12;
    $[69] = t13;
    $[70] = t14;
  } else {
    t14 = $[70];
  }
  return t14;
};
export const ChildGifts: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<GiftsSkeleton />}><ChildGiftsContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};